<template>
  <div>
    <RevButtonIcon
      v-if="isRounded"
      :alternative-text="buttonLabel"
      data-qa="my-favorites-toggle"
      :disabled="isDisabled"
      :icon="heartComponentVariant"
      variant="secondary"
      @click="handleClick"
    />

    <RevButton
      v-if="!isRounded"
      :aria-label="buttonLabel"
      class="h-full"
      data-qa="my-favorites-toggle"
      :disabled="isDisabled"
      size="small"
      variant="secondary"
      @click="handleClick"
    >
      <component :is="heartComponentVariant" class="my-8" />
    </RevButton>
  </div>
</template>

<script setup lang="ts">
import { navigateTo, useAsyncData, useRoute } from '#imports'
import { computed, ref, watch } from 'vue'

import type { MonetaryAmount } from '@backmarket/http-api'
import { useAuthStore } from '@backmarket/nuxt-layer-oauth/useAuthStore'
import { getAsyncDataKey } from '@backmarket/nuxt-module-http-v2/getAsyncDataKey'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import type { EventData } from '@backmarket/nuxt-module-tracking'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { RevButton } from '@ds/components/Button'
import { RevButtonIcon } from '@ds/components/ButtonIcon'
import { IconHeart } from '@ds/icons/IconHeart'
import { IconHeartFilled } from '@ds/icons/IconHeartFilled'
import { storeToRefs } from 'pinia'

import type { Favorite } from '~/api/favorites/types'
import { TRACKING_PAGE_TYPE_BY_ROUTE_NAME } from '~/constants/trackingPageType'
import { ROUTES } from '~/scopes/auth/route-names'

import {
  FAVORITES_STORAGE_PREFIX,
  FavoritesAction,
  LOG_ERROR_MESSAGE,
} from './FavoritesToggle'
import translations from './FavoritesToggle.translations'
import { useFavoritesToast } from './toast/useFavoritesToast'

const i18n = useI18n()
const authStore = useAuthStore()
const route = useRoute()
const logger = useLogger()
const { favoritesToast, isRemoveFavoriteToastOpen } = useFavoritesToast()
const { trackAddToMyFavorites, trackClick } = useTracking()
const $httpFetch = useHttpFetch()

const { authenticated: isAuthenticated } = storeToRefs(authStore)

const props = withDefaults(
  defineProps<{
    favoriteId?: string
    gradeId?: number
    isRounded?: boolean
    offerType: number | null
    price?: MonetaryAmount | null
    productUuid: string
    zone?: string
    trackingInfo?: Record<string, unknown>
  }>(),
  {
    favoriteId: undefined,
    gradeId: undefined,
    isRounded: false,
    price: undefined,
    offerType: 0,
    zone: 'details',
    trackingInfo: () => ({}),
  },
)

const emit = defineEmits(['removed'])

const isFavorite = ref(Boolean(props.favoriteId))
const localFavoriteId = ref(props.favoriteId)

function resetData() {
  isFavorite.value = Boolean(props.favoriteId)
  localFavoriteId.value = props.favoriteId
}

async function getFavoriteFromProduct() {
  if (isFavorite.value || !isAuthenticated.value) {
    return
  }

  const offerType = props.offerType ?? 0
  const query = {
    ...(props.gradeId !== undefined && {
      gradeId: props.gradeId,
    }),
    offerType,
  }

  const { data, error } = await useAsyncData(
    getAsyncDataKey('favorites-check', {
      productUuid: props.productUuid,
      gradeId: props.gradeId,
      offerType,
    }),
    () =>
      $httpFetch('/my-favorites/favorites/products/:productUuid', {
        method: 'GET',
        pathParams: { productUuid: props.productUuid },
        query,
      }),
  )

  if (error.value) {
    logger.error(LOG_ERROR_MESSAGE.CHECK, {
      error: error.value,
      owners: ['bot-squad-lifecycle-front'],
    })

    return
  }

  const [favorite] = data.value || ([] as Favorite[])

  if (!favorite) {
    return
  }

  localFavoriteId.value = favorite.favoriteId
  isFavorite.value = true
}

const isDisabled = computed(() => {
  return isRemoveFavoriteToastOpen.value || !props.gradeId || !props.price
})

const buttonLabel = computed(() => {
  return isFavorite.value
    ? i18n(translations.favoritesButtonRemoveLabel)
    : i18n(translations.favoritesButtonAddLabel)
})

const uniqueStorageKey = computed(() => {
  const key = `${FAVORITES_STORAGE_PREFIX}_${props.productUuid}_${props.gradeId}_${props.offerType}`
  const keyForRounded = `${key}_rounded`

  return props.isRounded ? keyForRounded : key
})

const heartComponentVariant = computed(() => {
  return isFavorite.value ? IconHeartFilled : IconHeart
})

function storePendingAction(pendingAction: FavoritesAction) {
  sessionStorage?.setItem(uniqueStorageKey.value, pendingAction)
}

async function requestAuthentication() {
  const next = route.fullPath

  await navigateTo({
    name: ROUTES.AUTH.LOGIN,

    query: {
      next,

      // needed for the info toast on Auth page
      info: 'my-favorites',
      bm_journey: 'favorites',
    },
  })
}

async function addToFavorites() {
  if (isFavorite.value) {
    favoritesToast.openAlreadySavedFavToast()

    return
  }

  const { productUuid, gradeId, price } = props

  if (!gradeId || !price) {
    logger.error(LOG_ERROR_MESSAGE.ADD, {
      owners: ['bot-squad-lifecycle-front'],
    })

    return
  }

  const offerType = props.offerType ?? 0

  try {
    const data = await $httpFetch('/my-favorites/favorites', {
      method: 'POST',
      body: { productUuid, gradeId, price, offerType },
    })

    const { favoriteId } = data ?? { favoriteId: '' }

    favoritesToast.openSavedFavToast()

    localFavoriteId.value = favoriteId

    isFavorite.value = true
  } catch (err) {
    logger.error(LOG_ERROR_MESSAGE.ADD, {
      error: err as Error,
      owners: ['bot-squad-lifecycle-front'],
    })
  }
}

async function removeFromFavorites() {
  const favoriteId = localFavoriteId.value ?? ''

  try {
    await $httpFetch('/my-favorites/favorites/:favoriteId', {
      method: 'DELETE',
      pathParams: { favoriteId },
    })

    localFavoriteId.value = ''
    isFavorite.value = false

    emit('removed')
  } catch (err) {
    logger.error(LOG_ERROR_MESSAGE.REMOVE, {
      error: err as Error,
      owners: ['bot-squad-lifecycle-front'],
    })

    // Rollback optimistic state when API call fails
    isFavorite.value = true
  }
}

function clearPendingAction() {
  sessionStorage?.removeItem(uniqueStorageKey.value)
}

function executePendingAction() {
  if (!process.client || !isAuthenticated.value) {
    return
  }

  const action = sessionStorage?.getItem(uniqueStorageKey.value)
  clearPendingAction()

  if (!action) {
    return
  }

  try {
    if (action === FavoritesAction.Add) {
      addToFavorites()
    } else if (action === FavoritesAction.Remove) {
      removeFromFavorites()
    }
  } catch (err) {
    const error = err as Error

    logger.error(LOG_ERROR_MESSAGE.PENDING, {
      error,
      owners: ['bot-squad-lifecycle-front'],
    })
  }
}

async function initialAction() {
  if (!isAuthenticated.value) {
    return
  }

  await getFavoriteFromProduct()
  executePendingAction()
}

function trackAddClick() {
  trackAddToMyFavorites(props.trackingInfo as EventData)
}

const pageType = computed(() => {
  const routeName = String(route.name)

  return TRACKING_PAGE_TYPE_BY_ROUTE_NAME[routeName]
})

function trackRemoveClick() {
  const name = 'remove_from_my_favorites'

  trackClick({ name, pageType: pageType.value, zone: props.zone })
}

async function handleClick() {
  const doTrackClick = isFavorite.value ? trackRemoveClick : trackAddClick
  doTrackClick()

  if (!isAuthenticated.value) {
    storePendingAction(FavoritesAction.Add)

    await requestAuthentication()

    return
  }

  if (isFavorite.value) {
    storePendingAction(FavoritesAction.Remove)

    favoritesToast.openRemoveFavToast(
      () => {
        // Undo callback
        isFavorite.value = true
        clearPendingAction()
      },
      () => {
        // Close callback
        executePendingAction()
      },
    )

    // optimistic UI: displays an empty heart right away,
    // even though the favorite has not been removed from the database yet
    isFavorite.value = false

    return
  }

  addToFavorites()
}

function optionUpdated() {
  resetData()
  initialAction()
}

watch(isAuthenticated, () => {
  optionUpdated()
})

watch(
  () => props.productUuid,
  () => optionUpdated(),
)

watch(
  // reacts to any item option change that is reflected in the URL
  () => route.fullPath,
  () => {
    optionUpdated()
  },
)

initialAction()
</script>
